import type {
  BrandAsset,
  BrandProfile,
  Client,
  ClientInput,
  User,
  PlatformPreset,
  PromptTemplate,
  PromptTemplateDetail,
  PromptTemplateVersion,
  TemplateDraft,
  ValidationIssue,
  GenerationBatch,
  GalleryItem,
  GenerationPreview,
  GenerationReference,
  GenerationCapabilities,
  StudioSettings,
  ManagedUser,
  ManagedSession,
} from "./types.js";

let csrfToken = "";

export class ApiClientError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData))
    headers.set("content-type", "application/json");
  if (init.method && !["GET", "HEAD"].includes(init.method) && csrfToken) {
    headers.set("x-csrf-token", csrfToken);
  }
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string } | string;
      code?: string;
      message?: string;
    };
    const nestedError =
      typeof payload.error === "object" ? payload.error : undefined;
    throw new ApiClientError(
      nestedError?.code ?? payload.code ?? "REQUEST_FAILED",
      nestedError?.message ?? payload.message ?? "Не удалось выполнить запрос.",
      response.status,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  getSettings() {
    return request<StudioSettings>("/settings");
  },
  saveSettings(input: StudioSettings) {
    const {
      defaultQuality,
      defaultOutputFormat,
      defaultCount,
      defaultPlatformId,
      maxConcurrentJobs,
      maxConcurrentPerUser,
      queuePaused,
      revision,
    } = input;
    return request<StudioSettings>("/settings", {
      method: "PUT",
      body: JSON.stringify({
        defaultQuality,
        defaultOutputFormat,
        defaultCount,
        defaultPlatformId,
        maxConcurrentJobs,
        maxConcurrentPerUser,
        queuePaused,
        revision,
      }),
    });
  },
  listUsers(page = 1) {
    return request<{ items: ManagedUser[]; total: number }>(
      `/settings/users?page=${page}`,
    );
  },
  createUser(input: {
    email: string;
    displayName: string;
    role: "admin" | "user";
    password: string;
  }) {
    return request<ManagedUser>("/settings/users", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateUser(input: ManagedUser) {
    const { email, displayName, role, status, updatedAt } = input;
    return request<ManagedUser>(`/settings/users/${input.id}`, {
      method: "PUT",
      body: JSON.stringify({ email, displayName, role, status, updatedAt }),
    });
  },
  resetUserPassword(id: string, password: string) {
    return request<void>(`/settings/users/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },
  listSessions(userId?: string) {
    return request<{ items: ManagedSession[] }>(
      `/settings/sessions${userId ? `?userId=${encodeURIComponent(userId)}` : ""}`,
    );
  },
  revokeSession(id: string) {
    return request<void>(`/settings/sessions/${id}/revoke`, { method: "POST" });
  },
  revokeOtherSessions(userId?: string) {
    return request<void>("/settings/sessions/revoke-others", {
      method: "POST",
      body: JSON.stringify({ userId }),
    });
  },
  async session() {
    const result = await request<{ user: User; csrfToken: string }>(
      "/auth/session",
    );
    csrfToken = result.csrfToken;
    return result.user;
  },
  async login(email: string, password: string) {
    const result = await request<{ user: User; csrfToken: string }>(
      "/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
      },
    );
    csrfToken = result.csrfToken;
    return result.user;
  },
  async logout() {
    await request<void>("/auth/logout", { method: "POST" });
    csrfToken = "";
  },
  listClients(query: URLSearchParams) {
    return request<{ items: Client[]; total: number }>(
      `/clients?${query.toString()}`,
    );
  },
  getClient(id: string) {
    return request<Client>(`/clients/${id}`);
  },
  createClient(input: ClientInput) {
    return request<Client>("/clients", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateClient(id: string, input: Partial<ClientInput>) {
    return request<Client>(`/clients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  archiveClient(id: string) {
    return request<Client>(`/clients/${id}/archive`, { method: "POST" });
  },
  restoreClient(id: string) {
    return request<Client>(`/clients/${id}/restore`, { method: "POST" });
  },
  getBrandProfile(id: string) {
    return request<BrandProfile>(`/clients/${id}/brand-profile`);
  },
  updateBrandProfile(id: string, input: BrandProfile) {
    const {
      primaryColors,
      secondaryColors,
      visualStyle,
      photographyStyle,
      compositionRules,
      lightingPreferences,
      preferredStyles,
      forbiddenStyles,
      realismLevel,
      additionalBrandInstructions,
    } = input;
    return request<BrandProfile>(`/clients/${id}/brand-profile`, {
      method: "PUT",
      body: JSON.stringify({
        primaryColors,
        secondaryColors,
        visualStyle,
        photographyStyle,
        compositionRules,
        lightingPreferences,
        preferredStyles,
        forbiddenStyles,
        realismLevel,
        additionalBrandInstructions,
      }),
    });
  },
  listAssets(clientId: string) {
    return request<{ items: BrandAsset[] }>(
      `/clients/${clientId}/assets?status=active`,
    );
  },
  uploadAsset(
    clientId: string,
    file: File,
    metadata: Record<string, string | boolean>,
  ) {
    const form = new FormData();
    for (const [key, value] of Object.entries(metadata))
      form.append(key, String(value));
    form.append("file", file);
    return request<BrandAsset>(`/clients/${clientId}/assets`, {
      method: "POST",
      body: form,
    });
  },
  updateAsset(id: string, input: Partial<BrandAsset>) {
    return request<BrandAsset>(`/assets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  archiveAsset(id: string) {
    return request<BrandAsset>(`/assets/${id}/archive`, { method: "POST" });
  },
  listTemplates(query: URLSearchParams) {
    return request<{ items: PromptTemplate[]; total: number }>(
      `/templates?${query.toString()}`,
    );
  },
  getTemplate(id: string) {
    return request<PromptTemplateDetail>(`/templates/${id}`);
  },
  createTemplate(input: Record<string, unknown>) {
    return request<PromptTemplate>("/templates", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  saveTemplateDraft(
    id: string,
    input: Partial<PromptTemplate & TemplateDraft>,
  ) {
    return request<PromptTemplateDetail>(`/templates/${id}/draft`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  validateTemplate(id: string) {
    return request<{ valid: boolean; issues: ValidationIssue[] }>(
      `/templates/${id}/validate`,
      { method: "POST", body: "{}" },
    );
  },
  previewTemplate(
    id: string,
    input: {
      clientId?: string | null;
      previewValues: Record<string, string | number | boolean | null>;
    },
  ) {
    return request<{
      text: string;
      valid: boolean;
      unresolved: string[];
      issues: ValidationIssue[];
    }>(`/templates/${id}/preview`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  publishTemplate(id: string, changelog = "") {
    return request<PromptTemplateVersion>(`/templates/${id}/publish`, {
      method: "POST",
      body: JSON.stringify({ changelog }),
    });
  },
  duplicateTemplate(
    id: string,
    input: {
      name: string;
      scope: "global" | "client";
      clientId?: string | null;
    },
  ) {
    return request<PromptTemplate>(`/templates/${id}/duplicate`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  archiveTemplate(id: string) {
    return request<PromptTemplate>(`/templates/${id}/archive`, {
      method: "POST",
      body: "{}",
    });
  },
  listTemplateVersions(id: string) {
    return request<PromptTemplateVersion[]>(`/templates/${id}/versions`);
  },
  getTemplateVersion(id: string, version: number) {
    return request<PromptTemplateVersion>(
      `/templates/${id}/versions/${version}`,
    );
  },
  listPlatformPresets(query = new URLSearchParams({ status: "active" })) {
    return request<{ items: PlatformPreset[]; total: number }>(
      `/platform-presets?${query.toString()}`,
    );
  },
  createPlatformPreset(input: Partial<PlatformPreset>) {
    return request<PlatformPreset>("/platform-presets", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updatePlatformPreset(id: string, input: Partial<PlatformPreset>) {
    return request<PlatformPreset>(`/platform-presets/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  archivePlatformPreset(id: string) {
    return request<PlatformPreset>(`/platform-presets/${id}/archive`, {
      method: "POST",
      body: "{}",
    });
  },
  uploadGenerationReference(file: File) {
    const form = new FormData();
    form.append("file", file);
    return request<GenerationReference>("/generation-references", {
      method: "POST",
      body: form,
    });
  },
  archiveGenerationReference(id: string) {
    return request<GenerationReference>(
      `/generation-references/${id}/archive`,
      { method: "POST", body: "{}" },
    );
  },
  getGenerationCapabilities() {
    return request<GenerationCapabilities>("/generation-capabilities");
  },
  previewGeneration(input: Record<string, unknown>, signal?: AbortSignal) {
    return request<GenerationPreview>("/generations/preview", {
      method: "POST",
      body: JSON.stringify(input),
      ...(signal ? { signal } : {}),
    });
  },
  createGeneration(input: Record<string, unknown>) {
    return request<GenerationBatch>("/generations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  listGenerations(query = new URLSearchParams()) {
    return request<{ items: GenerationBatch[]; total: number }>(
      `/generations?${query.toString()}`,
    );
  },
  getGeneration(id: string) {
    return request<GenerationBatch>(`/generations/${id}`);
  },
  cancelGeneration(id: string) {
    return request<GenerationBatch>(`/generations/${id}/cancel`, {
      method: "POST",
      body: "{}",
    });
  },
  retryGenerationItem(id: string) {
    return request(`/generation-items/${id}/retry`, {
      method: "POST",
      body: "{}",
    });
  },
  listGallery(query = new URLSearchParams()) {
    return request<{
      items: GalleryItem[];
      total: number;
      facets?: {
        clients: Array<{ id: string; name: string }>;
        templates: string[];
      };
    }>(`/gallery?${query.toString()}`);
  },
};
